import { useCallback } from 'react';
import { useSessionStore } from '../store/sessionStore';
import { RecognitionResult } from '../types/libras';

const WS_PROTOCOL = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
const WS_URL = process.env.REACT_APP_WS_URL || `${WS_PROTOCOL}//${window.location.host}/ws`;
const MAX_BUFFERED_BYTES = 512 * 1024;
const STABLE_SEND_MIN_CONFIDENCE = 0.82;
const STABLE_SEND_MIN_MARGIN = 0.12;

let sharedWs: WebSocket | null = null;
let sharedReconnectTimeout: ReturnType<typeof setTimeout> | null = null;
let shouldReconnect = true;

export const useWebSocket = () => {
  const {
    setConnectionStatus,
    addSignHistory,
    setCurrentSign,
    setRecognitionStatus,
    setPartialSign,
    addTranscriptWord,
  } = useSessionStore();
  
  const connect = useCallback(() => {
    if (sharedWs && (sharedWs.readyState === WebSocket.OPEN || sharedWs.readyState === WebSocket.CONNECTING)) {
      return;
    }

    shouldReconnect = true;
    setConnectionStatus('connecting');

    const ws = new WebSocket(WS_URL);
    sharedWs = ws;

    ws.onopen = () => {
      setConnectionStatus('connected');
    };

    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data) as RecognitionResult;
        const confidence = data.confidence ?? 0;
        const margin = data.margin ?? 0;
        const label = data.signLabel ?? '';
        const text = data.displayText ?? label;
        const timestamp = data.timestamp ?? Date.now();

        if (data.type === 'status') {
          const phase = data.detail === 'interpreting' ? 'interpreting'
            : data.detail === 'collecting' ? 'collecting'
            : 'no_hands';
          setRecognitionStatus(Boolean(data.handsDetected), phase, data.detail);
          return;
        }

        if (data.type === 'partial' && label) {
          setRecognitionStatus(true, 'partial', data.detail);
          setPartialSign({ label, text, confidence, margin, timestamp });
          setCurrentSign({ label, confidence });
          return;
        }

        if ((data.type === 'stable' || data.type === 'recognition') && label) {
          if (confidence < STABLE_SEND_MIN_CONFIDENCE || margin < STABLE_SEND_MIN_MARGIN) {
            setRecognitionStatus(true, 'rejected', 'low_reliability');
            setPartialSign(null);
            setCurrentSign(null);
            return;
          }

          setRecognitionStatus(true, 'stable', data.detail);
          setPartialSign(null);
          setCurrentSign({ label, confidence });
          addTranscriptWord({ label, text, confidence, timestamp });
          addSignHistory({ label, confidence, timestamp });
          return;
        }

        if (data.type === 'rejected') {
          setRecognitionStatus(Boolean(data.handsDetected), 'rejected', data.detail);
          setPartialSign(null);
          setCurrentSign(null);
          return;
        }

        if (data.type === 'error') {
          setRecognitionStatus(false, 'error', data.error ?? data.detail);
        }
      } catch {
        setRecognitionStatus(false, 'error', 'invalid_server_message');
      }
    };

    ws.onerror = () => {
      setConnectionStatus('error');
      setRecognitionStatus(false, 'error', 'websocket_error');
    };

    ws.onclose = () => {
      setConnectionStatus('disconnected');
      sharedWs = null;
      if (shouldReconnect) {
        sharedReconnectTimeout = setTimeout(connect, 3000);
      }
    };
  }, [
    addSignHistory,
    addTranscriptWord,
    setConnectionStatus,
    setCurrentSign,
    setPartialSign,
    setRecognitionStatus,
  ]);
  
  const disconnect = useCallback(() => {
    shouldReconnect = false;
    if (sharedReconnectTimeout) {
      clearTimeout(sharedReconnectTimeout);
      sharedReconnectTimeout = null;
    }

    if (sharedWs) {
      sharedWs.close(1000, 'client_disconnect');
      sharedWs = null;
    }

    setConnectionStatus('disconnected');
  }, [setConnectionStatus]);
  
  const sendFrame = useCallback((frameData: string) => {
    if (!sharedWs || sharedWs.readyState !== WebSocket.OPEN) return;
    if (sharedWs.bufferedAmount > MAX_BUFFERED_BYTES) return;

    sharedWs.send(JSON.stringify({
      type: 'frame',
      data: frameData,
      timestamp: Date.now(),
    }));
  }, []);
  
  return { connect, disconnect, sendFrame };
};
